import { C, T, type Painter } from './paint';
import type { ClientHost } from './mode';
import type { Pointer } from './platform';
import type { StrokeBrush } from '../shared/art/types';
import { defaultBrushSettings } from '../shared/art/brushes';
import { brushPreview } from './brush-preview';
import { STUDIO, studioLink } from './studio-ui';
import { StudioScroll } from './studio-scroll';
import { palettePreview } from './palette-preview';

export interface StudioChoice { label: string; detail?: string; colors?: readonly string[]; brush?: StrokeBrush }
/** App-drawn scrollable sheets shared by categories, presets and bundle contents. */
export class StudioPicker {
  private state?: { title: string; items: StudioChoice[]; selected: number; grid: boolean; resolve: (index: number|null)=>void };
  private consumed = new Set<number>();
  private scroll=new StudioScroll();
  constructor(private host: ClientHost) {}
  get active() { return !!this.state; }
  choose(title: string, items: (string|StudioChoice)[], selected=-1, grid=false): Promise<number|null> {
    this.close();this.scroll.reset();
    return new Promise(resolve => { this.state={title,items:items.map(v=>typeof v==='string'?{label:v}:v),selected,grid,resolve}; });
  }
  close(index: number|null=null) { this.scroll.cancel();const previous=this.state;this.state=undefined;previous?.resolve(index); }
  pointer(kind:'down'|'move'|'up'|'cancel',point:Pointer) {
    if(!this.active&&!this.consumed.has(point.id))return false;
    const local=point;
    if(this.active&&this.scroll.pointer(kind,local,this.host.paint.hits)){if(kind==='down')this.consumed.add(point.id);if(kind==='up'||kind==='cancel')this.consumed.delete(point.id);return true;}
    if(kind==='down'&&this.active) {
      this.consumed.add(point.id);
      const {x,y}=point;
      const hit=[...this.host.paint.hits].reverse().find(v=>!v.disabled&&x>=v.x&&x<=v.x+v.w&&y>=v.y&&y<=v.y+v.h);
      if(hit?.id.startsWith('picker-')) {this.host.paint.pressedId=hit.id;hit.action();}
    }
    if(kind==='up'||kind==='cancel')this.consumed.delete(point.id);
    return true;
  }
  render() {
    const s=this.state;if(!s)return;
    const p=this.host.paint,h=this.host.H-this.host.top,touch=Math.max(48,44/this.host.layout.scale);
    const panelWidth=this.host.tablet?Math.min(640,this.host.W-48):366,left=(this.host.W-panelWidth)/2;
    const cols=s.grid?2:1,rowH=s.grid?112:70,contentH=Math.ceil(s.items.length/cols)*rowH;
    const height=Math.min(h-98,106+contentH),y=h-height-12;
    const rect={x:left+14,y:y+82,w:panelWidth-28,h:height-100};
    this.scroll.frame('picker',rect,s.items.length,rowH,Date.now(),cols,60);
    p.hits=[];p.box(0,-this.host.top,p.width,this.host.H,'rgba(30,40,48,0.28)',0);
    p.hits.push({id:'picker-dismiss',label:'关闭选择面板',x:0,y:0,w:p.width,h,action:()=>this.close()});
    p.box(left,y,panelWidth,height,STUDIO.surface,24,STUDIO.line,12);
    p.hits.push({id:'picker-panel',label:s.title,x:left,y,w:panelWidth,h:height,action:()=>{}});
    p.text(s.title,left+18,y+29,T.cardTitle,STUDIO.dark,'700');
    p.text(s.grid?'轻点切换分类，上滑查看更多':'轻点查看或使用，上滑查看更多',left+18,y+57,T.caption,STUDIO.quiet);
    studioLink(p,'picker-close','关闭',left+panelWidth-76,y+8,58,touch,()=>this.close());
    p.hits.push({id:'picker-scroll',label:'上下滑动查看更多选项',...rect,drag:true,onScroll:delta=>this.scroll.wheel(delta),onAdjust:direction=>this.scroll.wheel(direction*80),action:()=>{}});
    const startHits=p.hits.length;p.ctx.save();p.ctx.beginPath();p.ctx.rect(rect.x,rect.y,rect.w,rect.h);p.ctx.clip();
    s.items.forEach((item,i)=>{
      const cell=(rect.w+10)/cols,index=i,x=rect.x+i%cols*cell,cy=y+82+Math.floor(i/cols)*rowH-this.scroll.offset,w=cell-10,ch=rowH-10,active=index===s.selected;
      if(cy+ch<rect.y||cy>rect.y+rect.h)return;
      p.box(x,cy,w,ch,active?STUDIO.pale:STUDIO.muted,14,active?'#96BCD5':undefined);
      if(s.grid){
        if(item.brush)brushPreview(this.host.platform,p.ctx,item.brush,STUDIO.accent,defaultBrushSettings(item.brush),x+10,cy+2,w-42,38);
        else if(item.colors)palettePreview(p,item.colors,x+12,cy+9,118,32);
        else p.icon('palette',x+26,cy+26,22,STUDIO.accent);
        p.text(item.label,x+12,cy+56,T.bodyStrong,active?STUDIO.accent:STUDIO.dark,'600');
        p.fit(item.detail??'',x+12,cy+80,w-24,T.caption,STUDIO.quiet);
      }else{
        p.fit(item.label,x+14,cy+20,w-54,T.bodyStrong,STUDIO.dark,'600');
        p.fit(item.detail??'轻点选择',x+14,cy+41,w-28,T.caption,STUDIO.quiet);
      }
      if(active){p.circle(x+w-18,cy+18,9,STUDIO.accent);p.icon('check',x+w-18,cy+18,10,C.white);}
      p.hits.push({id:`picker-option-${index}`,label:`${item.label}${item.detail?'，'+item.detail:''}${active?'，当前选择':''}`,x,y:cy,w,h:ch,action:()=>this.close(index)});
    });
    p.ctx.restore();this.scroll.clipHits(p.hits,startHits,44/this.host.layout.scale);
    if(contentH>rect.h){const track=rect.h-6,thumb=Math.max(24,track*rect.h/contentH),fraction=this.scroll.offset/Math.max(1,contentH-rect.h);p.box(left+panelWidth-11,rect.y+fraction*(track-thumb),3,thumb,'#C8D3D8',2);}

    const status=this.host.liveToolStatus();
    if(status){p.box(p.x(24),10,p.span(342),touch,STUDIO.surface,12);studioLink(p,'picker-return-room',`${status} · 返回房间`,p.x(24),10,p.span(342),touch,()=>{this.close();this.host.go(this.host.liveRoomActive()?'live-room':'lobby',-1);});}
  }
}

/** Explicit affordance: the selected category, option count, and a named switch action. */
export function studioCategory(p:Painter,id:string,label:string,count:number,y:number,h:number,action:()=>void) {
  p.box(p.x(24),y+2,p.span(342),h-4,STUDIO.surface,14,STUDIO.line);
  [0,1,2,3].forEach(i=>p.box(38+i%2*8,y+h/2-7+Math.floor(i/2)*8,5,5,STUDIO.accent,1));
  p.text(label,p.x(62),y+h/2-8,T.bodyStrong,STUDIO.dark,'600');
  p.text(`${count} 个分类可选`,p.x(62),y+h/2+11,T.note,STUDIO.quiet);
  p.box(p.x(252),y+h/2-15,p.span(101),30,STUDIO.pale,9);
  p.text('切换分类',p.x(265),y+h/2,T.caption,STUDIO.accent,'600');p.line([[p.x(342)-8,y+h/2-3],[p.x(342)-4,y+h/2+1],[p.x(342),y+h/2-3]],STUDIO.accent,1.5);
  p.hits.push({id,label:`切换分类，当前${label}`,x:p.x(24),y,w:p.span(342),h,action});
}
